import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "./UserAvatar";
import { ROLE_LABEL } from "@/lib/constants";
import { Camera, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ProfileDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  if (!user) return null;

  const pickFile = () => inputRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Foto profil harus berupa gambar.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran foto maksimal 5MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: rec } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put("/auth/me/foto", { file_id: rec.id });
      await refresh();
      toast.success("Foto profil diperbarui.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Hapus foto profil?")) return;
    setBusy(true);
    try {
      await api.put("/auth/me/foto", { file_id: null });
      await refresh();
      toast.success("Foto profil dihapus.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>Profil Saya</DialogTitle>
          <DialogDescription>Atur foto profil dan lihat informasi akun Anda.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          <UserAvatar user={user} size={128} />
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleUpload}
            data-testid="profile-foto-input"
          />
          <div className="flex flex-wrap gap-2 justify-center">
            <Button onClick={pickFile} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-upload-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
              {user.foto_id ? "Ganti Foto" : "Unggah Foto"}
            </Button>
            {user.foto_id && (
              <Button variant="outline" onClick={handleRemove} disabled={busy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="profile-remove-button">
                <Trash2 className="h-4 w-4 mr-2" /> Hapus
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500 text-center">PNG / JPG / WEBP · maks 5MB</p>
        </div>

        <div className="grid grid-cols-3 gap-3 text-sm border-t border-slate-200 pt-4">
          <InfoRow label="Nama" value={user.name} />
          <InfoRow label="Username" value={user.username} />
          <InfoRow label="Peran" value={ROLE_LABEL[user.role] || user.role} />
          {user.nik && <InfoRow label="NIK" value={user.nik} />}
          {user.no_hp && <InfoRow label="No. HP" value={user.no_hp} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="col-span-3 sm:col-span-1">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 break-words">{value || "-"}</div>
    </div>
  );
}
